import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';
import { shopV, type XmShop } from '../objectsXimen';

/**
 * Bangka Longshan Temple (艋舺龍山寺) and old Wanhua, north up:
 *
 *   herb shops ▒ 西 ┃ side ┃      main hall 正殿       ┃ side ┃ 東 ▒ Buddhist goods · medicine · ice
 *   ══ 青草巷 Herb Alley ══ 昌 ┃ bell ┃ courtyard: burner ┃ drum ┃ 巷 ══ old Wanhua lane ═══════════
 *   ▒ 華 ▒  shops          街 ┃  offerings · moon blocks · fortune sticks ┃ 剝皮寮 Bopiliao (red brick arcades)
 *   ▒ 西 ▒                    ┃ 虎門 ═══ front hall 三川殿 ═══ 龍門 ┃ ═══ Bopiliao lane ═══════════
 *   ▒ 街 ▒                       front plaza · waterfall-and-pond garden
 *   ═ gate ═══════════════ 廣州街 Guangzhou Street ════════════════════════════════════════
 *        艋舺公園 Bangka Park: chess under the banyans · MRT exit · pigeons · canoe sculpture
 *
 * The temple faces south: enter by the dragon door (east, on your right), leave by the tiger door
 * (west). Ground: '4' old Wanhua street, '5' temple stone slabs, '6' rooftops / temple roofs.
 */
const W = 90, H = 56;
const b = new MapBuilder(W, H, '6');

// Streets and lanes.
b.fill(20, 2, 6, 35, '4'); // 西昌街 Xichang Street
b.fill(0, 18, 20, 3, '4'); // 青草巷 Herb Alley
b.fill(7, 21, 5, 15, '4'); // 華西街 Huaxi Street
b.fill(62, 2, 2, 34, '4').fill(62, 16, 28, 3, '4'); // east lane and the old Wanhua lane
b.fill(62, 31, 28, 5, 'Q'); // 剝皮寮 Bopiliao
b.fill(0, 36, W, 1, 's');
// The temple: courtyard, passages through the front hall, the front plaza and its pond.
b.fill(28, 14, 32, 12, '5');
b.fill(34, 26, 2, 6, '5').fill(52, 26, 2, 6, '5');
b.fill(26, 32, 36, 5, '5');
b.fill(52, 33, 6, 2, '~').fill(58, 34, 4, 1, '~');
// 廣州街 Guangzhou Street and the crossings.
b.fill(0, 37, W, 5, 'r').fill(0, 39, W, 1, 'l');
b.fill(8, 37, 4, 5, '=').fill(42, 37, 4, 5, '=');
// 艋舺公園 Bangka Park.
b.fill(0, 42, W, 13, 'g').fill(0, 43, W, 2, 'p').fill(36, 42, 17, 9, 'X').fill(54, 45, 11, 6, 'X').fill(9, 45, 13, 6, 'X');
b.fill(25, 45, 2, 9, 'p').fill(68, 45, 2, 9, 'p').fill(0, 51, W, 2, 'p');
b.fill(2, 46, 5, 4, 'G').fill(78, 46, 8, 4, 'G').fill(28, 53, 7, 2, 'G').fill(54, 53, 9, 2, 'G');
b.fill(0, 55, W, 1, 'h');

const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
const tree = (tx: number, ty: number, kind: 'tree' | 'cherry' | 'xm_banyan' = 'tree'): Decor => ({ kind, ...at(tx, ty), solid: { x: px(tx) - 6, y: px(ty) - 6, w: 12, h: 6 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'lamp', ...at(tx, ty), solid: { x: px(tx) - 2, y: px(ty) - 3, w: 4, h: 3 } });
const decor: Decor[] = [];
const lights: Light[] = [];
const WARM = '255,200,120', RED = '255,110,80';

function facades(base: number, x0: number, list: [XmShop, number, number?][], h = 64): void {
  let x = x0;
  for (const [k, w, vs] of list) {
    decor.push({ kind: 'xm_shop', x: px(x) + px(w) / 2, y: px(base), w: px(w), h, variant: shopV(k) });
    lights.push({ x: px(x) + px(w) / 2, y: px(base) - 16, r: 30 + w * 6, color: WARM });
    if (vs !== undefined) {
      const sx = px(x) + (w > 4 ? 10 : 6);
      decor.push({ kind: 'xm_vsign', x: sx, y: px(base) + 1, variant: vs });
      lights.push({ x: sx, y: px(base) - 54, r: 30, color: RED });
    }
    x += w;
  }
}

// Herb Alley shops, the Huaxi corner, old Wanhua and Bopiliao.
facades(18, 2, [['herbTea', 5, 20], ['herbShop', 5], ['coolTea', 4], ['medicine', 4, 21]]);
facades(36, 0, [['danzai', 7, 24]]);
facades(36, 12, [['joss', 4, 25], ['teaHouse', 4, 26]]);
facades(16, 64, [['buddhist', 5, 27], ['medicine2', 5, 21], ['fruitIce', 5, 22], ['lantern', 4, 23], ['seal', 3], ['oldTea', 4]]);
facades(31, 64, [['bopiliao1', 7], ['bopiliao2', 6], ['bopiliao3', 7], ['bopiliao4', 6]], 80);

// --- The temple ------------------------------------------------------------------------------
decor.push({ kind: 'xm_mainHall', x: px(44), y: px(14), w: px(20) });
decor.push({ kind: 'xm_tower', x: px(30), y: px(19), w: px(4), variant: 0, solid: { x: px(28), y: px(14), w: px(4), h: px(5) } });
decor.push({ kind: 'xm_tower', x: px(58), y: px(19), w: px(4), variant: 1, solid: { x: px(56), y: px(14), w: px(4), h: px(5) } });
decor.push({ kind: 'xm_frontHall', x: px(44), y: px(32), w: px(36) });
// Lantern strings across the courtyard, offering tables, the fortune-slip cabinet, kneeling cushions.
decor.push({ kind: 'xm_lanterns', x: px(44), y: px(15), w: px(22), variant: 2 }, { kind: 'xm_lanterns', x: px(44), y: px(26), w: px(28) });
decor.push({ kind: 'xm_offerTable', x: px(46) + 8, y: px(16), variant: 1, solid: { x: px(45), y: px(16) - 10, w: px(3), h: 10 } });
decor.push({ kind: 'xm_slips', x: px(55), y: px(16), solid: { x: px(54), y: px(16) - 8, w: px(2), h: 8 } });
for (const tx of [37, 41, 47, 51]) decor.push({ kind: 'xm_cushion', x: px(tx), y: px(17) + 8 });
// Worshippers with incense facing the main hall, and a few walking visitors.
const pray: [number, number, number][] = [[38.2, 18.4, 1], [40.4, 18.8, 6], [46.8, 18.6, 10], [49.5, 18.2, 12], [36.6, 21.6, 14], [51.4, 21.8, 2], [41.2, 23.5, 7], [47.5, 23.8, 9]];
for (const [tx, ty, look] of pray) {
  const x = px(tx), y = px(ty);
  decor.push({ kind: 'xm_person', x, y, variant: 64 + look * 4 + 1, solid: { x: x - 4, y: y - 3, w: 8, h: 3 } });
}
// Front plaza: the waterfall rockery and koi pond (east), stone name tablet and incense stall (west).
decor.push({ kind: 'xm_rockery', x: px(60), y: px(34), w: px(4), solid: { x: px(58), y: px(32), w: px(4), h: px(2) } });
decor.push({ kind: 'xm_koi', x: px(55), y: px(33), w: px(6), h: px(2) });
decor.push({ kind: 'xm_stoneSign', x: px(28), y: px(34), w: px(4), solid: { x: px(26), y: px(32), w: px(4), h: px(2) } });
decor.push({ kind: 'xm_incenseStall', x: px(31) + 8, y: px(33) + 4, solid: { x: px(30) + 2, y: px(33) - 6, w: px(3) - 4, h: 10 } });
for (const [tx, ty, look, f] of [[38, 33.6, 3, 1], [47.4, 34.2, 11, 1], [40.6, 35.4, 5, 2], [33.4, 34.8, 8, 1]]) {
  const x = px(tx), y = px(ty);
  decor.push({ kind: 'xm_person', x, y, variant: look * 4 + f, solid: { x: x - 4, y: y - 3, w: 8, h: 3 } });
}
lights.push(
  { x: px(44), y: px(10), r: 110, color: WARM }, { x: px(44), y: px(28), r: 120, color: WARM }, { x: px(44), y: px(21), r: 70, color: '255,170,90' },
  { x: px(35), y: px(29), r: 50, color: RED }, { x: px(53), y: px(29), r: 50, color: RED }, { x: px(30), y: px(16), r: 50, color: RED }, { x: px(58), y: px(16), r: 50, color: RED },
  { x: px(36), y: px(22), r: 46, color: RED }, { x: px(44), y: px(22), r: 46, color: RED }, { x: px(52), y: px(22), r: 46, color: RED },
  { x: px(58), y: px(33), r: 50, color: '150,220,255' },
);

// --- Herb Alley, Huaxi Street, Xichang Street --------------------------------------------------
for (const [tx, v] of [[7, 0], [12, 1], [15, 2]] as const) decor.push({ kind: 'xm_herbPot', x: px(tx) + 8, y: px(19) + 4, variant: v, solid: { x: px(tx) + 1, y: px(19) - 4, w: 14, h: 8 } });
decor.push({ kind: 'xm_herbPot', x: px(3) + 8, y: px(20) + 12, variant: 2, solid: { x: px(3) + 1, y: px(20) + 4, w: 14, h: 8 } });
decor.push({ kind: 'xm_huaxiGate', x: px(9) + 8, y: px(36), w: px(7), solid: { x: px(6), y: px(35) + 4, w: px(1), h: 12 } });
decor.push({ kind: 'xm_lanterns', x: px(9) + 8, y: px(25), w: px(5), variant: 1 }, { kind: 'xm_lanterns', x: px(9) + 8, y: px(31), w: px(5), variant: 1 });
for (const [tx, ty, v] of [[7, 22, 0], [10, 24, 1], [7, 32, 2], [10, 34, 3]] as const) decor.push({ kind: 'xm_stall', x: px(tx) + 16, y: px(ty) + 16, variant: v, solid: { x: px(tx) + 2, y: px(ty) + 4, w: px(2) - 4, h: 12 } });
for (const [tx, ty] of [[21, 4], [21, 8], [21, 12], [24, 21], [24, 33]] as const) decor.push(lamp(tx, ty));
for (let i = 0; i < 6; i++) decor.push({ kind: 'scooter', x: px(20) + 8, y: px(22) + i * 12 + 10, variant: i % 3 ? 0 : 1, solid: { x: px(20) + 1, y: px(22) + i * 12 + 4, w: 14, h: 6 } });
for (let i = 0; i < 5; i++) decor.push({ kind: 'scooter', x: px(63) + 8, y: px(4) + i * 12 + 10, variant: i % 2, solid: { x: px(63) + 1, y: px(4) + i * 12 + 4, w: 14, h: 6 } });
decor.push({ kind: 'xm_person', x: px(5), y: px(20) + 4, variant: 6 * 4 + 0, solid: { x: px(5) - 4, y: px(20) + 1, w: 8, h: 3 } });
decor.push({ kind: 'xm_person', x: px(13) + 4, y: px(19) + 8, variant: 13 * 4 + 1, solid: { x: px(13), y: px(19) + 5, w: 8, h: 3 } });

// --- Old Wanhua lane and Bopiliao ------------------------------------------------------------
for (const tx of [66, 74, 82, 88]) decor.push({ kind: 'xm_oldLamp', ...at(tx, 35), solid: { x: px(tx) - 2, y: px(35) - 3, w: 4, h: 3 } });
for (const tx of [68, 80]) decor.push(lamp(tx, 19));
decor.push({ kind: 'xm_lanterns', x: px(76), y: px(18), w: px(24), variant: 1 });
decor.push({ kind: 'xm_rickshaw', x: px(80), y: px(34), solid: { x: px(79), y: px(34) - 6, w: px(2), h: 6 } });
decor.push({ kind: 'xm_person', x: px(77), y: px(33), variant: 4 * 4 + 1, solid: { x: px(77) - 4, y: px(33) - 3, w: 8, h: 3 } });
decor.push({ kind: 'xm_person', x: px(85) + 8, y: px(17) + 8, variant: 15 * 4 + 1, solid: { x: px(85) + 4, y: px(17) + 5, w: 8, h: 3 } });
for (const tx of [66, 74, 82, 88]) lights.push({ x: px(tx), y: px(35) - 24, r: 50, color: WARM });
for (const tx of [68, 80]) lights.push({ x: px(tx), y: px(19) - 26, r: 56, color: '255,224,150' });
lights.push({ x: px(76), y: px(17), r: 120, color: RED }, { x: px(9) + 8, y: px(28), r: 90, color: RED }, { x: px(9) + 8, y: px(34), r: 70, color: RED });

// --- Guangzhou Street ------------------------------------------------------------------------
for (let tx = 0; tx < W; tx += 18) decor.push({ kind: 'xm_traffic', x: px(tx + 9), y: px(37) + 1, w: px(18), h: px(5), variant: 1 });
const rail = (x0: number, x1: number, ty: number): Decor => ({ kind: 'xm_rail', x: (px(x0) + px(x1)) / 2, y: px(ty) + 2, w: px(x1 - x0), solid: { x: px(x0), y: px(ty) - 1, w: px(x1 - x0), h: 4 } });
decor.push(rail(0, 8, 37), rail(12, 42, 37), rail(46, W, 37), rail(0, 8, 42), rail(12, 42, 42), rail(46, W, 42));
decor.push({ kind: 'trafficLight', ...at(41, 37) }, { kind: 'trafficLight', ...at(46, 43) }, { kind: 'trafficLight', ...at(12, 43) });

// --- Bangka Park -----------------------------------------------------------------------------
decor.push(tree(14, 46, 'xm_banyan'), tree(20, 50, 'xm_banyan'), tree(32, 49, 'xm_banyan'), tree(57, 52, 'xm_banyan'), tree(76, 50, 'xm_banyan'));
for (const [tx, ty] of [[3, 51], [8, 53], [23, 47], [29, 47], [52, 53], [65, 47], [72, 53], [83, 51], [87, 46], [62, 53]] as const) decor.push(tree(tx, ty));
decor.push({ kind: 'xm_chessTable', x: px(12), y: px(48), variant: 1, solid: { x: px(11), y: px(48) - 8, w: px(2), h: 8 } });
decor.push({ kind: 'xm_chessTable', x: px(19), y: px(48), variant: 2, solid: { x: px(18), y: px(48) - 8, w: px(2), h: 8 } });
const watchers: [number, number, number, number][] = [[14.6, 46.9, 4, 0], [17.4, 47, 15, 0], [11, 46.6, 13, 0], [20.6, 46.7, 0, 0], [13.2, 49.8, 9, 1], [16.4, 50, 6, 1]];
for (const [tx, ty, look, f] of watchers) {
  const x = px(tx), y = px(ty);
  decor.push({ kind: 'xm_person', x, y, variant: look * 4 + f, solid: { x: x - 4, y: y - 3, w: 8, h: 3 } });
}
// Tai chi in the east lawn.
for (const [tx, ty, look] of [[79, 47.4, 4], [81.5, 47.6, 13], [84, 47.3, 0], [80.4, 49.4, 15], [83, 49.5, 9]] as const) {
  const x = px(tx), y = px(ty);
  decor.push({ kind: 'xm_person', x, y, variant: 128 + look * 4 + 0, solid: { x: x - 4, y: y - 3, w: 8, h: 3 } });
}
for (const tx of [6, 24, 36, 52, 66, 86]) decor.push(lamp(tx, 45));
for (const tx of [6, 24, 36, 52, 66, 86]) lights.push({ x: px(tx), y: px(45) - 26, r: 56, color: '255,224,150' });
decor.push({ kind: 'xm_bench', x: px(30), y: px(46), solid: { x: px(29), y: px(46) - 6, w: 32, h: 6 } }, { kind: 'xm_bench', x: px(58), y: px(54) + 8, solid: { x: px(57), y: px(54) + 2, w: 32, h: 6 } });
decor.push({ kind: 'xm_bench', x: px(74), y: px(46), solid: { x: px(73), y: px(46) - 6, w: 32, h: 6 } });
decor.push({ kind: 'xm_toilet', x: px(88), y: px(55), w: px(4), solid: { x: px(86), y: px(53), w: px(4), h: px(2) } });
decor.push({ kind: 'bikeRack', x: px(48), y: px(50) + 4 });
decor.push({ kind: 'trashBin', ...at(41, 50), solid: { x: px(41) - 4, y: px(50) - 4, w: 8, h: 4 } });
decor.push({ kind: 'vendingDeco', x: px(39), y: px(46) + 4, solid: { x: px(38) + 8, y: px(46), w: 16, h: 4 } });
lights.push({ x: px(44), y: px(47), r: 60, color: '200,230,255' });

export const longshan: MapDef = {
  id: 'longshan',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e9dcc3', wallLight: '#f6ecd8', wainscot: '#8a5c3a', wallTop: '#3a2a20' },
  doors: [],
  decor,
  areas: [
    { key: 'xmTemple', rect: { x: px(26), y: 0, w: px(36), h: px(37) } },
    { key: 'xmHerbAlley', rect: { x: 0, y: px(14), w: px(20), h: px(7) } },
    { key: 'xmHuaxi', rect: { x: px(6), y: px(21), w: px(7), h: px(16) } },
    { key: 'xmBopiliao', rect: { x: px(62), y: px(19), w: px(28), h: px(18) } },
    { key: 'xmOldWanhua', rect: { x: px(62), y: 0, w: px(28), h: px(19) } },
    { key: 'xmBangka', rect: { x: 0, y: px(42), w: px(W), h: px(14) } },
    { key: 'longshan', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: { mrt: { x: px(44), y: px(45) + 8, facing: 'up' } },
};
